/**
 * resolveOptionalKoReward move — resolves a pending optional-KO-then-reward
 * player choice (WP-248 / D-24019).
 *
 * Called by the active player after an optional-ko-reward hero ability parked a
 * PendingOptionalKoReward on G.pendingOptionalKoRewards (FIFO). The player
 * either declines (no KO, no reward) or KOs exactly one card from their hand or
 * discard pile, in which case the reward is dispatched to the ALREADY-BUILT
 * reward executor (rescue / draw / attack / recruit via executeSingleEffect) —
 * no re-implementation.
 *
 * Atomicity is exact: the reward fires ONLY after the KO actually removes a
 * card from its zone. Decline pops the queue with no KO and no reward. A
 * stale/absent target is a silent no-op that leaves the queue intact so the
 * player can resubmit (the block-all guard guarantees a valid target still
 * exists while pending).
 *
 * Unlike koHeroChoice.resolve.ts, this move destructures `context` because the
 * dispatched `draw` reward needs ctx.random for its deck reshuffle.
 *
 * No registry imports. No .reduce(). Moves never throw.
 */
import { moveCardFromZone } from './zoneOps.js';
import { koCard } from '../board/ko.logic.js';
import { recordOwnedCardKoed } from '../hero/deferredConditionalGrants.js';
import { executeSingleEffect } from '../hero/heroEffects.execute.js';
import { cardCountsAsShieldHero } from '../hero/effectiveTeams.logic.js';
import { gainOfficerToHand } from './recruitOfficer.js';
import { pushLog } from '../log/logPush.js';
import { formatCardRef, describeKoSourceZone } from '../log/logDisplay.js';
import { WOUND_EXT_ID } from '../setup/pilesInit.js';
/**
 * Whether any optional-KO-reward choice is currently pending.
 *
 * Single predicate imported by the block-all action-move guards and the
 * getLegalMoves short-circuit. `undefined` and `[]` both mean no pending
 * choice (mirrors hasPendingKoHeroChoice, D-24007).
 *
 * @param G - The game state to inspect (not mutated).
 * @returns true when the pending optional-KO-reward queue holds at least one entry.
 */
export function hasPendingOptionalKoReward(G) {
    return (G.pendingOptionalKoRewards?.length ?? 0) > 0;
}
/**
 * Resolves the FRONT pending optional-KO-reward choice.
 *
 * Atomic sequence (HARD — exact order, mirrors koHeroChoice.resolve.ts):
 *   1. Validate args — exactly { decline: true } XOR { zone, cardId }; an
 *      invalid shape is a silent no-op (queue intact).
 *   2. Validate the front pending entry — non-empty queue, front.playerID match.
 *   3. { decline } → front-pop ONLY, no KO, no reward; logs one neutral line.
 *   4. { zone, cardId } → the card must be present in playerZones[pid][zone]
 *      NOW (recomputed fresh, no snapshot). Absent/stale → silent no-op, queue
 *      intact (resubmit).
 *   5. Remove the card from its zone → G.ko = koCard(...).
 *   6. THEN dispatch the reward via the existing executor (no re-implementation).
 *   7. Front-pop (queue.shift()) LAST.
 *
 * Any failure before step 5 ABORTS the reward (no KO ⇒ no reward; no KO via the
 * { zone, cardId } path without a reward dispatch). Moves never throw.
 *
 * // why: D-24019 — the reward fires only on an actual KO (atomic); FIFO
 * front-pop; the reward is dispatched to the existing executor (no re-impl).
 *
 * @param context - boardgame.io move context with G, playerID, and the rest
 *   (ctx, events, random, log) spread into `context` for the reward dispatch.
 * @param args - the decline flag or the { zone, cardId } to KO.
 */
export function resolveOptionalKoReward({ G, playerID, ...context }, args) {
    // Step 1: Validate args — exactly one of { decline: true } / { zone, cardId }.
    // why: a client may submit the move with no payload (undefined) or null; reading a field off
    // either throws a TypeError, and moves never throw — reject before any field read.
    if (args === null || typeof args !== 'object') {
        return;
    }
    const isDecline = args.decline === true;
    const zone = args.zone;
    const cardId = args.cardId;
    const isKoRequest = (zone === 'hand' || zone === 'discard' || zone === 'inPlay') &&
        typeof cardId === 'string' &&
        cardId.length > 0;
    // why: exactly-one-shape — both present (decline AND zone/cardId) or neither
    // present is a malformed payload and a silent no-op.
    if (isDecline === isKoRequest) {
        return;
    }
    // Step 2: Validate the front pending entry — front-only resolution (no index
    // in the payload, so a non-front entry can never be targeted).
    const queue = G.pendingOptionalKoRewards;
    if (queue === undefined || queue.length === 0) {
        return;
    }
    const front = queue[0];
    if (front.playerID !== playerID) {
        return;
    }
    // Step 3: Decline → front-pop, no KO, no reward, and one log line.
    // why: a silent decline left the card's play line with nothing after it, which reads
    // the same as an ability that never fired (Dangerous Rescue in match 452o26A0iXw).
    // Outcome 'neutral': the player chose not to pay, nothing failed.
    if (isDecline) {
        pushLog(G, `Player ${playerID} chose not to KO a card for ${formatCardRef(G.cardDisplayData, front.sourceCardId)}'s ability.`, 'neutral', front.sourceCardId);
        queue.shift();
        return;
    }
    // why: WP-667 / D-24480 — enforce the entry's koZones scope. Absent = the D-24442
    // wide set (hand ∪ discard ∪ inPlay), so existing rewarded entries are unchanged; a
    // no-reward Radioactive Riot entry sets ['hand','discard'], so a submitted 'inPlay'
    // zone (which the card does not permit) is rejected as a silent no-op (queue intact,
    // resubmit). The projection lists an empty inPlay set, so a well-behaved client never
    // submits it; this guards a hand-built / malformed payload.
    if (front.koZones !== undefined && !front.koZones.includes(zone)) {
        return;
    }
    // why: WP-681 / D-24498 — enforce the entry's koTeamFilter. ABSENT = no team
    // restriction (every existing entry is unchanged); 'shield' (Battlefield Promotion)
    // rejects a KO target that is not a S.H.I.E.L.D. Hero (the Officer token itself counts,
    // via cardCountsAsShieldHero) as a silent no-op (queue intact, resubmit). The projection
    // lists only S.H.I.E.L.D. Heroes, so a well-behaved client never submits a non-shield
    // target; this guards a hand-built / malformed payload.
    if (front.koTeamFilter === 'shield' && !cardCountsAsShieldHero(G, cardId)) {
        return;
    }
    // why: WP-767 / D-24600 — enforce the entry's koHeroesOnly. ABSENT = no filter (every
    // existing entry is unchanged); true (Snarling Fangs' "one of your Heroes") rejects a Wound,
    // which is not a Hero, as a silent no-op (queue intact, resubmit). The projection omits
    // Wounds, so a well-behaved client never submits one; this guards a malformed payload.
    if (front.koHeroesOnly === true && cardId === WOUND_EXT_ID) {
        return;
    }
    // Step 4: KO request — the chosen card must be present in the named zone right
    // now (no eligible snapshot is stored; eligibility is recomputed fresh).
    const playerZones = G.playerZones[playerID];
    if (!playerZones) {
        return;
    }
    const targetZone = zone;
    const targetCardId = cardId;
    const moveResult = moveCardFromZone(playerZones[targetZone], [], targetCardId);
    if (!moveResult.found) {
        // why: invalid/stale target is a no-op that leaves the queue intact so the
        // player resubmits; the block-all guard guarantees a valid target exists.
        return;
    }
    // Step 5: Mutate — shorten the source zone and KO the chosen card.
    playerZones[targetZone] = moveResult.from;
    G.ko = koCard(G.ko, targetCardId);
    recordOwnedCardKoed(G, playerID, targetCardId, targetZone);
    // why: D-24399 — the KO is the paid cost of the optional-KO-reward; log it here
    // (before the Step-6 reward line) so the spent card is observable in the game
    // log instead of silently vanishing, mirroring the Master Strike KO / heroEffectKo
    // phrasing. Outcome 'neutral' because it is a cost, not the payoff.
    // why: D-24693 — the zone wording is shared with every other KO line; 'the cards they
    // played this turn' names the in-play source (D-24442) so a KO'd played card reads clearly.
    const koSourceZoneLabel = describeKoSourceZone(targetZone);
    pushLog(G, `Player ${playerID} KO'd ${formatCardRef(G.cardDisplayData, targetCardId)} from ${koSourceZoneLabel} for ${formatCardRef(G.cardDisplayData, front.sourceCardId)}'s ability.`, 'neutral');
    // Step 6: THEN dispatch the reward by REUSING the existing executor — no
    // re-implementation. The reward's own logging (e.g. D-24017 for rescue) is the
    // only reward log; this move adds no duplicate. `context` carries ctx.random
    // for the draw reward's reshuffle.
    // why: WP-681 / D-24498 — the gain-officer-hand variant (Battlefield Promotion) gains a
    // S.H.I.E.L.D. Officer from supply to the player's HAND. It is NOT a HeroKeyword, so it
    // must never reach executeSingleEffect; dispatch it via the dedicated gainOfficerToHand
    // helper. The reward is pure upside, so it auto-resolves on KO (no second choice, the
    // reveal-from-hand precedent); an empty Officer supply no-ops (gainOfficerToHand returns
    // false) with a log line so the player sees why no Officer arrived.
    if (front.rewardType === 'gain-officer-hand') {
        if (gainOfficerToHand(G, playerID)) {
            pushLog(G, `Player ${playerID} gained a S.H.I.E.L.D. Officer to their hand from ${formatCardRef(G.cardDisplayData, front.sourceCardId)}'s ability.`, 'applied', front.sourceCardId);
        }
        else {
            pushLog(G, `Player ${playerID} KO'd a S.H.I.E.L.D. Hero for ${formatCardRef(G.cardDisplayData, front.sourceCardId)}, but the S.H.I.E.L.D. Officer supply was empty, so no Officer was gained.`, 'neutral', front.sourceCardId);
        }
    }
    else if (front.rewardType !== 'none') {
        // why: WP-667 / D-24480 — the NO-REWARD variant (Radioactive Riot) parks
        // `rewardType: 'none'`; the KO itself is the whole effect (deck-thinning), so skip
        // the dispatch. 'none' is not a HeroKeyword, so it must never reach executeSingleEffect.
        executeSingleEffect(G, context, playerID, front.sourceCardId, {
            type: front.rewardType,
            magnitude: front.rewardMagnitude,
        });
    }
    // Step 7: Front-pop LAST (front-pop = Array.shift), mirroring WP-242.
    queue.shift();
}
//# sourceMappingURL=optionalKoReward.resolve.js.map